import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import SvgIcon from "./SvgIcon";

// How long to wait, after the pointer leaves, before actually closing - long
// enough to cross the gap between the button and the list (moving diagonally
// down into a lower option) without the menu closing under the cursor first.
const CLOSE_DELAY_MS = 200;

const PrimaryDropdown = ({
  summaryText,
  options,
  onSelectFn,
  alignRight,
  isAdminConsole = false,
  // Opt-in only (default false, every other call site untouched) - the
  // Milestones "+Add" (MilestoneRowHeader.js) needed its leading icon to
  // read `currentColor` (so a gradient-text-clip trick can recolor it, same
  // as PhaseRowHeader.js's "+Add Training/Nutrition Phase") instead of the
  // plain black/white-filtered <img> every other dropdown here still uses.
  gradientIcon = false,
}) => {
  const [opened, setOpened] = useState(false);
  const closeTimerRef = useRef(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  /**
   * Open or close the ul when the user clicks the button
   * @param {*} e
   */
  const handleClick = (e) => {
    e.preventDefault();
    clearCloseTimer();
    setOpened(true);
  };

  /**
   * Close the ul if the focus is lost
   * @param {*} e
   */
  const handleBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setOpened(false);
    }
  };

  /**
   * When the user selects the option
   * @param {*} option
   */
  const handleOptionClick = (option) => {
    clearCloseTimer();
    onSelectFn(option.value);
    setOpened(false);
  };

  return (
    <div
      className={`dropdown ${opened ? "open" : ""} `}
      data-align-right={alignRight ? true : undefined}
      onBlur={handleBlur}
      onMouseEnter={(e) => {
        e.preventDefault();
        clearCloseTimer();
        setOpened(true);
      }}
      onMouseLeave={(e) => {
        e.preventDefault();
        closeTimerRef.current = setTimeout(
          () => setOpened(false),
          CLOSE_DELAY_MS
        );
      }}
      role="menu"
      tabIndex={0}
      style={isAdminConsole ? { paddingBottom: 8 } : {}}
    >
      <button
        onClick={handleClick}
        aria-haspopup="menu"
        aria-expanded={opened}
        data-button-type="primary"
      >
        {gradientIcon ? (
          <SvgIcon
            src="/img/new-icons/add.svg"
            useCurrentColor
            classNames="icon-sm mobile-hidden"
          />
        ) : (
          <SvgIcon
            src="/img/new-icons/add.svg"
            noFilterOnLight={true}
            alt="add exercise"
            classNames="mobile-hidden"
          />
        )}
        <p>{summaryText}</p>
        <SvgIcon
          src={opened ? "/img/svg/angle-up.svg" : "/img/svg/angle-down.svg"}
        />
      </button>

      <ul className={opened ? "opened dropdown-menu" : "dropdown-menu"}>
        {options.map((option, i) => (
          <li key={option.value} role="none">
            <button
              role="menuitem"
              onMouseDown={(e) => {
                e.preventDefault();
                handleOptionClick(option);
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                handleOptionClick(option);
              }}
            >
              {option.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

PrimaryDropdown.propTypes = {
  summaryText: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.any.isRequired,
    })
  ).isRequired,
  onSelectFn: PropTypes.func.isRequired,
  alignRight: PropTypes.bool,
  isAdminConsole: PropTypes.bool,
  gradientIcon: PropTypes.bool,
};

export default PrimaryDropdown;
